import { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  Polygon,
  Popup,
  Tooltip,
} from "react-leaflet";
import ReactLeafletDriftMarker from "react-leaflet-drift-marker";
import io from "socket.io-client";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import SpeedGauge from "./SpeedGauge";
import VehiclePopup from "./VehiclePopup";
import StreetView from "./StreetView";

import "../../styles/map.css";

const { BaseLayer, Overlay } = LayersControl;

/* ---------------------------------------------------------
   Build dynamic vehicle icon
--------------------------------------------------------- */
const getVehicleIcon = (isFlashing) =>
  L.divIcon({
    html: `
      <div class="marker-wrapper">
        ${isFlashing ? '<div class="pulse-ring pulse-active"></div>' : ""}
        <img src="/car_icon.png" class="car-marker" />
      </div>
    `,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

/* ---------------------------------------------------------
   Geofence Style
--------------------------------------------------------- */
const geofenceStyle = {
  color: "#2563eb",
  weight: 2,
  opacity: 0.6,
  fillColor: "#3b82f6",
  fillOpacity: 0.15,
};

/* ---------------------------------------------------------
   Unified telemetry → vehicle object mapper
   Ensures initial + socket updates are consistent
--------------------------------------------------------- */
function mapTelemetryToVehicle(telemetry, registryItem, existing = {}) {
  const t = telemetry;

  return {
    id: telemetry.vehicleId || existing.id,

    // Registry information
    name:
      registryItem?.registrationNumber ||
      registryItem?.registration ||
      existing.name ||
      `Vehicle ${telemetry.vehicleId}`,
    driverName: registryItem?.driverName || existing.driverName || "N/A",

    // Core location
    lat: parseFloat(t.lat),
    lon: parseFloat(t.lon),
    alt: t.alt ?? existing.alt ?? null,

    // Motion
    speed: t.speed ?? existing.speed ?? 0,
    movement: t.movement ?? existing.movement ?? false,
    course: t.course ?? existing.course ?? 0,

    // Status
    ignitionOn: t.ignitionOn ?? existing.ignitionOn ?? false,
    GSMSignal: t.GSMSignal ?? existing.GSMSignal ?? 0,
    unitBatteryLevel: t.unitBatteryLevel ?? existing.unitBatteryLevel ?? null,
    extBatteryVoltage:
      t.extBatteryVoltage ?? existing.extBatteryVoltage ?? null,

    // CAN & Fuel
    LVCANTotalFuelUsed:
      t.LVCANTotalFuelUsed ?? existing.LVCANTotalFuelUsed ?? null,
    LVCANFuelLevel: t.LVCANFuelLevel ?? existing.LVCANFuelLevel ?? null,
    LVCANEngineRPM: t.LVCANEngineRPM ?? existing.LVCANEngineRPM ?? null,
    LVCANVehicleDistance:
      t.LVCANVehicleDistance ?? existing.LVCANVehicleDistance ?? null,
    LVCANDoorStatus: t.LVCANDoorStatus ?? existing.LVCANDoorStatus ?? null,
    LVCANEngineTemperature:
      t.LVCANEngineTemperature ??
      existing.LVCANEngineTemperature ??
      null,

    // Distance
    totalOdometer: t.totalOdometer ?? existing.totalOdometer ?? null,
    tripOdometer: t.tripOdometer ?? existing.tripOdometer ?? null,

    // Metadata
    source: t.source || existing.source || "Unknown",
    type: t.type || existing.type || "Unknown",
    iButton: t.iButton ?? existing.iButton ?? null,
    timeStamp: t.timeStamp || existing.timeStamp || null,

    // Road info
    roadName: t.roadName || existing.roadName || null,
    roadType: t.roadType || existing.roadType || null,
    maxSpeed:
      t.maxSpeed ?? existing.maxSpeed ?? null,

    // UI
    isFlashing: existing.isFlashing ?? false,
  };
}

/* ---------------------------------------------------------
   Main Component
--------------------------------------------------------- */
export default function MapView() {
  const [vehicles, setVehicles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [selected, setSelected] = useState(null);

  const socketRef = useRef(null);
  const flashTimers = useRef({});
  const selectedRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const WS_URL = process.env.REACT_APP_WS_URL || "http://localhost:5000";
  const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    "pk.eyJ1IjoibWFwbGFwYW5lIiwiYSI6ImNtaDN5aGwybDFnY3AwOHFwbzJrOTF4c2cifQ.sQB_UMSe27x9JRdllElxmw";

  /* ---------------------------------------------------------
     INITIAL DATA LOAD
  --------------------------------------------------------- */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [vehiclesRes, telemetryRes, geofenceRes] = await Promise.all([
          fetch(`${API_URL}/v1/vehicles`),
          fetch(`${API_URL}/v1/telemetry/latest`),
          fetch(`${API_URL}/v1/geofences`),
        ]);

        const [vehicleList, telemetry, geos] = await Promise.all([
          vehiclesRes.json(),
          telemetryRes.json(),
          geofenceRes.json(),
        ]);

        setRegistry(vehicleList);

        // Combine registry + telemetry
        const mapped = telemetry
          .filter((v) => v.telemetries?.length)
          .map((v) => {
            const t = v.telemetries[0];
            const reg = vehicleList.find((x) => x.id === v.id);
            return mapTelemetryToVehicle(
              { ...t, vehicleId: v.id },
              reg,
              {}
            );
          });

        setVehicles(mapped);

        // Format geofences
        setGeofences(
          geos.map((g) => ({
            id: g.id,
            name: g.name,
            polygon: Array.isArray(g.polygon)
              ? g.polygon.map(([lat, lon]) => [lat, lon])
              : [],
          }))
        );
      } catch (err) {
        console.error("Failed to load initial data:", err);
      }
    };

    fetchInitialData();
  }, [API_URL]);

  /* ---------------------------------------------------------
     SOCKET.IO REAL-TIME UPDATES
  --------------------------------------------------------- */
  useEffect(() => {
    socketRef.current = io(WS_URL, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () => {
      console.log("[Map] Socket connected:", socketRef.current.id);
    });

    socketRef.current.on("telemetry_all", (data) => {
      const lat = parseFloat(data.lat);
      const lon = parseFloat(data.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      setVehicles((prev) => {
        const exists = prev.find((v) => v.id === data.vehicleId);
        const reg = registry.find((x) => x.id === data.vehicleId);

        // Build unified updated object
        const updated = mapTelemetryToVehicle(
          data,
          reg,
          { ...exists, isFlashing: true }
        );

        // Update selected vehicle if needed
        if (selectedRef.current?.id === data.vehicleId) {
          setSelected((old) => mapTelemetryToVehicle(data, reg, old));
        }

        // Pulse icon
        if (flashTimers.current[data.vehicleId]) {
          clearTimeout(flashTimers.current[data.vehicleId]);
        }
        flashTimers.current[data.vehicleId] = setTimeout(() => {
          setVehicles((prev) =>
            prev.map((v) =>
              v.id === data.vehicleId ? { ...v, isFlashing: false } : v
            )
          );
        }, 3000);

        // Insert or update
        if (exists) {
          return prev.map((v) =>
            v.id === data.vehicleId ? updated : v
          );
        } else {
          return [...prev, updated];
        }
      });
    });

    return () => {
      socketRef.current?.close?.();
      Object.values(flashTimers.current).forEach(clearTimeout);
    };
  }, [registry]);

  /* ---------------------------------------------------------
     Track selected ref
  --------------------------------------------------------- */
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  /* ---------------------------------------------------------
     Throttled Street View refresh (3 sec)
  --------------------------------------------------------- */
  useEffect(() => {
    if (!selected || !selected.roadName) return;

    const interval = setInterval(() => {
      setSelected((prev) =>
        prev
          ? {
            ...prev,
            lat: prev.lat,
            lon: prev.lon,
            course: prev.course,
          }
          : prev
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [selected]);

  /* ---------------------------------------------------------
     RENDER
  --------------------------------------------------------- */
  return (
    <>
      <MapContainer
        center={[-28.6, 24.657847]}
        zoom={6}
        style={{ height: "100vh", width: "100%" }}
      >
        <LayersControl position="topright">
          <BaseLayer checked name="Light">
            <TileLayer
              url={`https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            />
          </BaseLayer>

          <BaseLayer name="Dark">
            <TileLayer
              url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            />
          </BaseLayer>

          <BaseLayer name="Satellite">
            <TileLayer
              url={`https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            />
          </BaseLayer>

          {/* Geofences */}
          <Overlay checked name="Geofences">
            <>
              {geofences.map((g) => (
                <Polygon
                  key={g.id}
                  positions={g.polygon}
                  pathOptions={geofenceStyle}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                    {g.name}
                  </Tooltip>
                </Polygon>
              ))}
            </>
          </Overlay>
        </LayersControl>

        {/* VEHICLES */}
        {vehicles.map((v) => (
          <ReactLeafletDriftMarker
            key={v.id}
            position={[v.lat, v.lon]}
            duration={1200}
            icon={getVehicleIcon(v.isFlashing)}
            eventHandlers={{
              click: () => {
                setSelected((prev) => {
                  // Find the fully populated vehicle from state
                  const full = vehicles.find((x) => x.id === v.id);
                  return full ? { ...full } : v;
                });
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -15]} opacity={1}>
              {v.name}
            </Tooltip>

            {selected?.id === v.id && (
              <Popup autoPan={true} maxWidth={420}>
                <VehiclePopup vehicle={v} />
              </Popup>
            )}
          </ReactLeafletDriftMarker>
        ))}
      </MapContainer>

      {/* SPEED GAUGE OVERLAY */}
      {selected && (
        <div className="speed-gauge-overlay">
          <SpeedGauge value={selected.speed || 0} />

          {selected.roadName && (
            <div className="road-info-box">
              <p><b>{selected.roadName}</b></p>
              <p>
                {selected.roadType
                  ? selected.roadType.replace(/_/g, " ")
                  : "—"}{" "}
                •{" "}
                {selected.maxSpeed
                  ? `${selected.maxSpeed} km/h`
                  : "No limit data"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* STREET VIEW HUD */}
      {selected && selected.roadName && (
        <div className="streetview-overlay">
          <StreetView
            lat={selected.lat}
            lon={selected.lon}
            heading={selected.course || 0}
          />
        </div>
      )}
    </>
  );
}
